import { ClockIcon } from '@heroicons/react/24/solid'
import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import { FC, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useSelectedProjectPremises } from '@/api/models/main/hooks/useSelectedProjectPremises'
import { RequestByIdApiType } from '@/api/models/requests'
import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import { ServerValidationError } from '@/api/shared/types'
import { PassAutosFormBlock } from '@/components/entities/passes/ui/PassForms/FormBlock/PassAutosFormBlock'
import { RequestCommentFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestCommentFormBlock'
import { RequestDatePickerFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestDatePickerFormBlock'
import { RequestRoomPickerFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestRoomPickerFormBlock'
import { useFormDirty } from '@/components/entities/requests/ui/hooks/useFormDirty'
import {
  formatAutosData,
  getDefaultAutos,
  getDefaultRoom
} from '@/components/entities/requests/ui/model/utils'
import { DrawerFormLayout } from '@/components/shared/drawer-form-layout'

import {
  AutoPassPropertyFormData,
  AutosApiData,
  generateAutoPassPropertySchema
} from '../../../requests/ui/model/schemas'

type Props = {
  onFormDirty: (_: boolean) => void
  pass?: RequestByIdApiType
  onSubmit: (data: AutosApiData) => Promise<void>
  passError?: AxiosError<ServerValidationError> | null
  isPending?: boolean
}

const PropertiesPassForm: FC<Props> = ({
  onFormDirty,
  pass,
  onSubmit,
  passError,
  isPending
}) => {
  const { rooms } = useSelectedProjectPremises()

  const form = useForm<AutoPassPropertyFormData>({
    defaultValues: {
      room: undefined,
      description: undefined,
      autos: [{ number: '', model: '', isStandard: true }]
    },
    mode: 'onSubmit',
    resolver: yupResolver(generateAutoPassPropertySchema(false, false, false))
  })

  const { formState, handleSubmit, reset } = form

  useFormDirty(onFormDirty, formState)

  useEffect(() => {
    reset({
      room: getDefaultRoom(rooms, pass?.premiseId),
      autos: getDefaultAutos(pass, undefined),
      description: pass?.properties as string
    })
  }, [pass, reset, rooms])

  const handleFormSubmit = async (data: AutoPassPropertyFormData) => {
    const formattedData = {
      ...formatAutosData(data),
      properties: data.description,
      description: undefined
    }
    await onSubmit(formattedData)
  }

  return (
    <DrawerFormLayout
      onSubmit={handleSubmit(handleFormSubmit)}
      isPending={isPending}
      submitButtonClassName="mt-8"
    >
      <div className="mb-24">
        <div className="mb-24 flex items-center gap-x-8 rounded-12 border border-bronze-40 p-16 xs:items-start">
          <ClockIcon className="h-20 w-20 flex-shrink-0 text-bronze-40" />
          <p className="text-16">Услуга доступна каждый день с 8:00 до 20:00</p>
        </div>
        <div className="form-block-cover mb-16 grid grid-cols-1 gap-24 p-32 lg:grid-cols-1 lg:p-16 xs:gap-16 xs:p-16">
          <div className="form-block-cover">
            <div className="grid grid-cols-2 gap-24 xs:grid-cols-1 xs:gap-16">
              <FormProvider {...form}>
                <RequestDatePickerFormBlock passError={passError} />
              </FormProvider>
              <FormProvider {...form}>
                <RequestRoomPickerFormBlock />
              </FormProvider>
            </div>
          </div>
        </div>
        <FormProvider {...form}>
          <PassAutosFormBlock
            typeAlias={RequestTypeAlias.PropertiesPass}
            passError={passError}
          />
        </FormProvider>
        <FormProvider {...form}>
          <RequestCommentFormBlock
            className="mb-24 sm:px-16"
            title="Укажите наименование и количество имущества, которое собираетесь перевозить"
            placeholder="Напишите список имущества"
            inputTestId="properties-list-input-id"
            counterTestId="properties-list-length-counter-id"
          />
        </FormProvider>

        <p className="font-pro text-16 sm:text-14">
          Нажимая кнопку «Отправить», обязуюсь убрать за собой упаковочную тару,
          не загромождать проходы в местах общего пользования. Сохранность
          интерьера и оборудования здания по маршруту движения гарантирую.
          В случае порчи обязуюсь возместить ущерб.
        </p>
      </div>
    </DrawerFormLayout>
  )
}

export { PropertiesPassForm }
